// noinspection GraphQLUnresolvedReference

import { useMutation } from '@tanstack/react-query'

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import type { MutationOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'
import { impresoraAsignacionFragmentAst } from '../fragments/impresoraFragment.ts'
import { ApiImpresoraAsignacionInputProps, ImpresoraAsignacionProps } from '../interfaces/impresora.ts'

const query = /* GraphQL */ gqlAst`
  ${impresoraAsignacionFragmentAst}

  mutation IMPRESORA_ASIGNACION_UPSERT(
    $codigoSucursal: Int!
    $codigoPuntoVenta: Int
    $input: ImpresoraAsignacionInput!
  ) {
    impresoraAsignacionUpsert(codigoSucursal: $codigoSucursal, codigoPuntoVenta: $codigoPuntoVenta, input: $input) {
      ...impresoraAsignacionFields
    }
  }
`

export const MUTATION_KEY_IMPRESORA_ASIGNACION_UPSERT = ['impresora-asignacion-upsert'] as const

export interface ApiImpresoraAsignacionUpsertParams {
  codigoSucursal: number
  /** Punto de venta de la asignación; null u omitido = valor por defecto de toda la sucursal */
  codigoPuntoVenta?: number | null
  /** Documento, formato, copias e impresora a asignar */
  input: ApiImpresoraAsignacionInputProps
}

/**
 * Servicio para crear o actualizar la asignación de impresora de un documento,
 * para un punto de venta o para toda la sucursal.
 * @param params Sucursal, punto de venta (opcional) y datos de la asignación.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @returns La asignación guardada.
 * @author isi-template
 */
export const apiImpresoraAsignacionUpsert = async (
  params: ApiImpresoraAsignacionUpsertParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ImpresoraAsignacionProps> => {
  try {
    const data = await graphqlClient.request<{ impresoraAsignacionUpsert: ImpresoraAsignacionProps }>({
      document: query,
      variables: { ...params, codigoPuntoVenta: params.codigoPuntoVenta ?? null },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraAsignacionUpsert
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Hook de mutación para crear o actualizar una asignación de impresora.
 * Para refrescar al terminar:
 * `onSuccess: () => Promise.all([invalidateImpresoraAsignacionListado(), invalidateImpresoraPuntoVenta()])`.
 * @param options Opciones de React Query (onSuccess, onError, ...).
 * @author isi-template
 */
export const useImpresoraAsignacionUpsert = (
  options?: MutationOpts<ImpresoraAsignacionProps, ApiImpresoraAsignacionUpsertParams>,
) => {
  return useMutation({
    mutationKey: MUTATION_KEY_IMPRESORA_ASIGNACION_UPSERT,
    ...options,
    mutationFn: (params: ApiImpresoraAsignacionUpsertParams) => apiImpresoraAsignacionUpsert(params),
  })
}
